/**
 * generate-password 1.6.1
 * (c) 2014-2021 Brendan Ashworth
 * Released under the terms of the MIT license
 * https://github.com/brendanashworth/generate-password/blob/master/src/generate.js
 *
 * Also inspired by Rubinous:
 * https://stackoverflow.com/a/54363513
 */

// character sets
const lowercase = 'abcdefghijklmnopqrstuvwxyz';
const uppercase = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ';
const numbers = '0123456789';
const symbols = '!@#$%^&*()+_-=}{[]|:;"/?.><,`~';

const lookalikes = /[ilLI|`oO0]/g;
const rules = [
  { name: 'lowercase', regexp: /[a-z]/ },
  { name: 'uppercase', regexp: /[A-Z]/ },
  { name: 'numbers', regexp: /[0-9]/ },
  { name: 'symbols', regexp: /[!@#$%^&*()+_\-=}{[\]|:;"/?.><,`~]/ }
];

const getRandomValues = (count, length) => {
  // we only need one array, since the length won't change and getRandomValues overwrites everything
  const random = new Uint16Array(count * 1.25);
  // to avoid modulo bias, `max` is the largest multiple of `length` less than 2^16
  const max = Math.floor(2 ** 16 / length) * length;

  let values = [];
  while (values.length < count) {
    // generate new random values, creating a new array with the results < max
    values = crypto.getRandomValues(random).filter(i => i < max);
  }
  // use Array.from() to make it a normal JS array
  return Array.from(values.slice(0, count).map(x => x % length));
};

const generatePassword = (options, pool) => {
  // main generation logic
  const random = getRandomValues(options.length, pool.length)
    // and then replace each value with a character from the pool
    .reduce((res, rand) => res + pool[rand], '');

  // if we need to check to see that it follows some rules
  if (options.strict) {
    // iterate over each rule, checking to see if the password works.
    if (
      !rules.every(rule => {
        // If the option is not checked, ignore it.
        if (!options[rule.name]) {
          return true;
        }

        // treat symbol differently if explicit string is provided
        if (rule.name === 'symbols' && typeof options[rule.name] === 'string') {
          // Create a regular expression from the provided symbols
          return new RegExp(`[${options[rule.name]}]`).test(random);
        }

        // run the regex on the password and return whether or not it matches.
        return rule.regexp.test(random);
      })
    ) {
      /**
       * original implementation has a potential infinite recursion issue, so
       * just return `null` if it fails and the user can use `while` or whatever
       */
      return null;
    }
  }

  return random;
};

/**
 * Public interfaces for random password generation using `window.crypto`.
 * Returns a string, or null if the user has required and the password fails
 * strict rule validation.
 *
 * @param {Object} opts - config options
 * @param {number} [opts.length=16] - length of generated string
 * @param {boolean} [opts.numbers=true] - allow numbers
 * @param {string} [opts.exclude=''] - string of invalid characters
 * @param {boolean} [opts.uppercase=true] - include uppercase characters
 * @param {boolean} [opts.lowercase=true] - include lowercase characters
 * @param {boolean|string} [opts.symbols=false] - include symbols (can be string of symbols)
 * @param {boolean} [opts.lookalikes=false] - include lookalike characters
 * @param {boolean} [opts.strict=false] - ensure that the password passes some tests
 * @returns {string|null} randomly generated password
 */
export function password(opts) {
  const options = {
    length: 16,
    numbers: true,
    exclude: '',
    uppercase: true,
    lowercase: true,
    symbols: false,
    lookalikes: false,
    strict: false,
    ...(opts ?? {})
  };

  if (options.strict) {
    const minLength =
      1 +
      (options.numbers ? 1 : 0) +
      (options.symbols ? 1 : 0) +
      (options.uppercase ? 1 : 0);

    if (minLength > options.length) {
      throw new TypeError('Length must correlate with strict guidelines.');
    }
  }

  // generate initial character pool
  let pool = [
    options.lowercase ? lowercase : '',
    options.uppercase ? uppercase : '',
    options.numbers ? numbers : ''
  ].join('');

  /**
   * if the user's passed symbols, use those, otherwise if the user's okayed
   * symbols, use the default set.
   */
  if (options.symbols) {
    pool += typeof options.symbols === 'string' ? options.symbols : symbols;
  }

  // remove lookalike characters
  if (!options.lookalikes) {
    pool = pool.replace(lookalikes, '');
  }

  if (options.exclude) {
    pool = pool.replace(
      // split into single characters and |
      new RegExp(options.exclude.split('').join('|'), 'g'),
      ''
    );
  }

  if (options.strict) {
    let res = null;
    while (!res) {
      res = generatePassword(options, pool);
    }
    return res;
  }

  return generatePassword(options, pool);
}
